// Switzer is not published on Fontsource/npm; it is loaded via Fontshare's
// hosted CSS — see <link> in index.html and the rationale in DESIGN.md §3.
import "@fontsource-variable/fraunces/index.css";
import "@fontsource-variable/jetbrains-mono/index.css";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "./theme/global.css";

import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { SessionProvider } from "./features/auth/SessionProvider";
import { WorkspaceProvider } from "./features/workspace/WorkspaceProvider";
import { IntlProviderWrapper } from "./i18n/IntlProviderWrapper";
import { routeTree } from "./routeTree.gen";
import { theme } from "./theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createRouter({ routeTree, context: { queryClient } });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <ModalsProvider>
          <IntlProviderWrapper>
            <Notifications position="bottom-center" />
            <SessionProvider>
              <WorkspaceProvider>
                <RouterProvider router={router} />
              </WorkspaceProvider>
            </SessionProvider>
          </IntlProviderWrapper>
        </ModalsProvider>
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>,
);
